import Tool from '../core/Tool';
import tinyMCELoader from '../utils/TinyMCELoader';

class WYSIWYG extends Tool {
    constructor({id, updateFunction, config}) {
        super(id, updateFunction, config);

        // Merge config with defaults (preserve all fields including templateProperties)
        this.config = {
            ...config, // Keep all original config fields
            content: config.content || this.config.content || '<p>Start typing here...</p>',
            format: config.format || this.config.format || 'div',
            features: config.features || this.config.features || {
                blocks: true,
                bold: true,
                italic: true,
                underline: true,
                strikethrough: true,
                formatBlock: true,
                lists: true,
                links: true,
                alignment: true,
                indentation: true,
                textColor: true,
                backgroundColor: true,
                fontSize: true,
                fontFamily: true
            }
        };

        this.editorId = `wysiwyg-${this.id}`;
        this.toolbar = null;

        // Detect if this is a template with properties
        this.isTemplate = !!this.config.templateProperties;
        this.templateId = this.config.templateId || null;
        this.templateProperties = this.config.templateProperties || [];
        this.currentTemplateInstanceId = null; // Track which template instance is being edited

        // Debug logging for template blocks
        if (this.isTemplate) {
            console.log('[WYSIWYG Constructor] Template block detected:', {
                id: id,
                templateId: this.templateId,
                propertiesCount: this.templateProperties.length,
                properties: this.templateProperties
            });
        }
    }

    /**
     * Dynamic settings getter - computes settings based on block type
     * For template mode: shows template-specific properties
     * For standard mode: shows format selector
     */
    get settings() {
        // Try to detect template properties if not already detected (safety net)
        if (!this.hasTemplateProperties) {
            this.detectTemplateProperties();
        }

        console.log('[WYSIWYG] settings getter called', {
            blockId: this.id,
            isTemplate: this.isTemplate,
            hasTemplateProperties: this.hasTemplateProperties,
            propertiesCount: this.templateProperties?.length,
            hasContentToInsert: !!this.config._templateContentToInsert
        });

        // If we have template properties, return template-specific settings
        // Use hasTemplateProperties (set in init) instead of isTemplate (used for preview mode)
        if (this.hasTemplateProperties && this.templateProperties.length > 0) {
            console.log('[WYSIWYG] Returning template mode settings', {
                blockId: this.id,
                templateId: this.templateId,
                propertiesCount: this.templateProperties.length
            });
            return this.buildTemplateSettings();
        }

        // Standard WYSIWYG settings
        console.log('[WYSIWYG] Returning standard WYSIWYG settings', {
            blockId: this.id,
            reason: !this.hasTemplateProperties ? 'hasTemplateProperties=false' : 'properties empty'
        });
        return [
            {
                name: 'format',
                label: 'Block Format',
                html: `<select @change="trigger('${this.id}', 'format', $event.target.value)">
                    <option value="div" ${this.config.format === 'div' ? 'selected' : ''}>Plain</option>
                    <option value="p" ${this.config.format === 'p' ? 'selected' : ''}>Paragraph</option>
                    <option value="pre" ${this.config.format === 'pre' ? 'selected' : ''}>Preformatted</option>
                </select>`
            }
        ];
    }

    static toolbox() {
        return {
            name: 'Rich Text',
            icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M57.7 193l9.4 16.4c8.3 14.5 21.9 25.2 38 29.8L163 255.7c17.2 4.9 29 20.6 29 38.5v39.9c0 11 6.2 21 16 25.9s16 14.9 16 25.9v39c0 15.6 14.9 26.9 29.9 22.6c16.1-4.6 28.6-17.5 32.7-33.8l2.8-11.2c4.2-16.9 15.2-31.4 30.3-40l8.1-4.6c15-8.5 24.2-24.5 24.2-41.7v-8.3c0-12.7-5.1-24.9-14.1-33.9l-3.9-3.9c-9-9-21.2-14.1-33.9-14.1H257c-11.1 0-22.1-2.9-31.8-8.4l-34.5-19.7c-4.3-2.5-7.6-6.5-9.2-11.2c-3.2-9.6 1.1-20 10.2-24.5l5.9-3c6.6-3.3 14.3-3.9 21.3-1.5l23.2 7.7c8.2 2.7 17.2-.4 21.9-7.5c4.7-7 4.2-16.3-1.2-22.8l-13.6-16.3c-10-12-9.9-29.5 .3-41.3l15.7-18.3c8.8-10.3 10.2-25 3.5-36.7l-2.4-4.2c-3.5-.2-6.9-.3-10.4-.3C163.1 48 84.4 108.9 57.7 193zM464 256c0-36.8-9.6-71.4-26.4-101.5L412 164.8c-15.7 6.3-23.8 23.8-18.5 39.8l16.9 50.7c3.5 10.4 12 18.3 22.6 20.9l29.1 7.3c1.2-9 1.8-18.2 1.8-27.5zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"/></svg>',
            category: 'Basic',
            allowRawPreview: false
        };
    }

    init(editor) {
        // Call parent init
        super.init(editor);

        // CRITICAL: Restore template properties BEFORE any initialization
        // This ensures hasTemplateProperties = true before any block selection events fire
        // Note: We use hasTemplateProperties instead of isTemplate to avoid triggering
        // the static preview mode in editorRender()
        if (this.config._templatePropertiesToInsert && Array.isArray(this.config._templatePropertiesToInsert)) {
            this.templateProperties = this.config._templatePropertiesToInsert;
            this.templateId = this.config._templateIdToInsert;
            this.hasTemplateProperties = true; // Use this instead of isTemplate
            console.log('[WYSIWYG init] Pre-restored template properties:', {
                blockId: this.id,
                templateId: this.templateId,
                propertiesCount: this.templateProperties.length,
                hasTemplateProperties: this.hasTemplateProperties
            });
        }

        // Initialize RichTextLoader on the textarea once it's in the DOM
        if (editor && editor.$nextTick) {
            editor.$nextTick(() => {
                const textarea = document.getElementById(this.editorId);
                if (textarea) {
                    // Check if already initialized (e.g., after reload)
                    const wrapper = textarea.parentElement;
                    const existingEditor = wrapper?.querySelector('[contenteditable="true"]');

                    if (!existingEditor) {
                        // First time initialization
                        richTextLoader.init(`#${this.editorId}`, {
                            features: this.config.features,
                            height: 200,
                            onChange: (content) => {
                                // Update block config when content changes
                                if (this.config) {
                                    this.config.content = content;
                                }
                            }
                        });
                        // After RichTextLoader initialization, insert template content if present
                        if (this.config._templateContentToInsert) {
                            // Wait for next tick to ensure RichTextLoader is fully initialized
                            setTimeout(() => {
                                this.insertTemplateContent(
                                    this.config._templateContentToInsert,
                                    this.config._templateIdToInsert,
                                    this.config._templatePropertiesToInsert
                                );
                                delete this.config._templateContentToInsert;
                                delete this.config._templateIdToInsert;
                                delete this.config._templatePropertiesToInsert;
                            }, 0);
                        }
                    } else {
                        console.log('[WYSIWYG] RichTextLoader already initialized for', this.editorId);
                    }
                }
            });
        }
    }

    /**
     * Detect and activate template mode if properties exist in config
     * Called by settings getter as a safety net for late-arriving properties
     * @returns {boolean} True if template properties were detected and activated
     */
    detectTemplateProperties() {
        // Check if template properties exist in config but flag not set
        if (!this.hasTemplateProperties &&
            this.config.templateProperties &&
            Array.isArray(this.config.templateProperties) &&
            this.config.templateProperties.length > 0) {

            this.hasTemplateProperties = true;
            this.templateProperties = this.config.templateProperties;
            this.templateId = this.config.templateId;

            console.log('[WYSIWYG] Template properties detected post-init:', {
                blockId: this.id,
                templateId: this.templateId,
                propertiesCount: this.templateProperties.length
            });

            return true;
        }
        return false;
    }

    /**
     * Programmatically insert template content into the editor
     * Uses the same wrapping logic as RichTextLoader's drop handler
     * @param {string} htmlContent - Template HTML to insert
     * @param {string} templateId - Optional template ID for tracking
     * @param {Array} templateProperties - Optional template properties for Properties panel
     */
    insertTemplateContent(htmlContent, templateId = null, templateProperties = null) {
        console.log('[WYSIWYG] insertTemplateContent called with:', {
            htmlContentLength: htmlContent?.length,
            templateId: templateId,
            templateProperties: templateProperties,
            isArray: Array.isArray(templateProperties),
            propertiesCount: templateProperties?.length
        });

        const editorDiv = document.getElementById(`${this.editorId}-editor`);
        if (!editorDiv) {
            console.error('[WYSIWYG] Cannot insert template: editor div not found');
            return;
        }

        // Get RichTextLoader instance - it's exported as window.richTextLoader (not window.AlpineBlocks.richTextLoader)
        const richTextLoader = window.richTextLoader;
        if (!richTextLoader) {
            console.error('[WYSIWYG] Cannot insert template: RichTextLoader not available');
            return;
        }

        // Use centralized method to create template wrapper (DRY - single source of truth)
        const { wrapper: templateWrapper, instanceId } = richTextLoader.createTemplateWrapper(
            htmlContent,
            templateId
        );

        // Template properties already restored in init() - no action needed here
        console.log('[WYSIWYG] insertTemplateContent: properties already set', {
            blockId: this.id,
            hasTemplateProperties: this.hasTemplateProperties,
            propertiesCount: this.templateProperties?.length
        });

        // Clear placeholder content if editor is empty
        if (editorDiv.innerHTML === `<p>${this.config.placeholder || 'Start typing here...'}</p>`) {
            editorDiv.innerHTML = '';
        }

        // Insert template wrapper into editor
        editorDiv.appendChild(templateWrapper);

        // Add escape paragraph after for cursor positioning
        const escapeParagraph = document.createElement('p');
        escapeParagraph.innerHTML = '<br>';
        editorDiv.appendChild(escapeParagraph);

        // Sync to textarea
        const textarea = document.getElementById(this.editorId);
        if (textarea && richTextLoader) {
            textarea.value = richTextLoader.cleanHTML(editorDiv.innerHTML);
        }

        console.log('[WYSIWYG] Template content inserted:', instanceId);
    }

    /**
     * Inject CSS styles for drop indicators and animations
     * This is called once globally to avoid Alpine.js parsing issues
     */
    static injectDropStyles() {
        if (!document.getElementById('wysiwyg-drop-animation')) {
            const style = document.createElement('style');
            style.id = 'wysiwyg-drop-animation';
            style.textContent = `
                @keyframes dropPulse {
                    0%, 100% {
                        opacity: 0.9;
                        transform: scaleY(1);
                        box-shadow: 0 0 12px rgba(59, 130, 246, 0.8);
                    }
                    50% {
                        opacity: 1;
                        transform: scaleY(1.3);
                        box-shadow: 0 0 20px rgba(59, 130, 246, 1);
                    }
                }

                .richtext-drop-indicator {
                    display: block !important;
                    height: 6px !important;
                    background: linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6) !important;
                    background-size: 200% 100% !important;
                    border-radius: 4px !important;
                    margin: 4px 0 !important;
                    pointer-events: none !important;
                    position: relative !important;
                    animation: dropPulse 1s ease-in-out infinite, gradientShift 2s linear infinite !important;
                    z-index: 1000 !important;
                }

                .richtext-drop-indicator::before {
                    content: 'DROP HERE' !important;
                    position: absolute !important;
                    top: -20px !important;
                    left: 50% !important;
                    transform: translateX(-50%) !important;
                    background: #3b82f6 !important;
                    color: white !important;
                    padding: 2px 12px !important;
                    border-radius: 12px !important;
                    font-size: 11px !important;
                    font-weight: 600 !important;
                    letter-spacing: 1px !important;
                    white-space: nowrap !important;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4) !important;
                }

                @keyframes gradientShift {
                    0% {
                        background-position: 0% 50%;
                    }
                    100% {
                        background-position: 200% 50%;
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }

    /**
     * Process template HTML to convert <!-- drop --> markers into drop zones
     * @param {string} html - Template HTML content
     * @param {string} parentId - Parent template instance ID
     * @returns {string} Processed HTML with drop zones
     */
    static processDropZones(html, parentId) {
        if (!html || typeof html !== 'string') {
            return html;
        }

        // Find all <!-- drop --> markers
        const dropMarkerRegex = /<!--\s*drop\s*-->/gi;
        const matches = [...html.matchAll(dropMarkerRegex)];

        if (matches.length === 0) {
            return html; // No drop zones, return as-is
        }


        // Replace each <!-- drop --> with a styled drop zone div
        let processedHTML = html;
        let zoneIndex = 0;

        processedHTML = processedHTML.replace(dropMarkerRegex, () => {
            const zoneId = `${parentId}-dropzone-${zoneIndex++}`;
            return `<div class="richtext-nested-dropzone"
                         data-zone-id="${zoneId}"
                         data-parent-id="${parentId}"
                         contenteditable="false"
                         style="min-height: 80px;
                                border: 2px dashed #d1d5db;
                                border-radius: 8px;
                                margin: 12px 0;
                                padding: 20px;
                                text-align: center;
                                color: #9ca3af;
                                background: #f9fafb;
                                transition: all 0.2s ease;
                                cursor: pointer;
                                position: relative;">
                <span style="font-size: 14px; font-weight: 500; pointer-events: none;">Drop template here</span>
            </div>`;
        });

        return processedHTML;
    }

    /**
     * Clean HTML by removing temporary elements like drop indicators
     * and converting drop zones back to comments
     */
    static cleanHTML(html) {
        // Create temp div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Remove all drop indicators
        const dropIndicators = tempDiv.querySelectorAll('.richtext-drop-indicator');
        if (dropIndicators.length > 0) {
        }
        dropIndicators.forEach(indicator => indicator.remove());

        // Convert EMPTY nested drop zones back to <!-- drop --> comments
        const dropZones = tempDiv.querySelectorAll('.richtext-nested-dropzone');
        let emptyCount = 0;

        dropZones.forEach(zone => {
            // Check if drop zone is empty (only has the placeholder text)
            const textContent = zone.textContent.trim();
            const hasOnlyPlaceholder = textContent === 'Drop template here' || textContent === '';

            if (hasOnlyPlaceholder) {
                // Replace with comment
                const comment = document.createComment(' drop ');
                zone.parentNode.replaceChild(comment, zone);
                emptyCount++;
            }
            // If zone has content (dropped templates), keep it as-is
        });

        if (emptyCount > 0) {
        }

        return tempDiv.innerHTML;
    }

    editorRender() {
        // Template mode: render as static HTML preview (no rich text editor)
        // Templates are controlled via Properties panel, not direct editing
        if (this.isTemplate) {
            console.log('[WYSIWYG.editorRender] Rendering template as static preview', {
                blockId: this.id,
                templateId: this.templateId
            });
            return `<div class="template-preview" style="width: 100%; min-height: 200px; padding: 1rem; border: 1px solid #e5e7eb; border-radius: 0.5rem; background: white;">
                ${this.config.content || '<p>Template content loading...</p>'}
            </div>`;
        }

        // Standard WYSIWYG: rich text editor
        // Escape HTML entities for textarea content
        const escapedContent = (this.config.content || '<p>Start typing here...</p>')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

        return `<textarea id="${this.editorId}"
                          style="width: 100%; min-height: 200px; display: none;">${escapedContent}</textarea>`;
    }

    /**
     * Build template-specific settings from template properties
     */
    buildTemplateSettings() {
        console.log('[WYSIWYG] buildTemplateSettings called', {
            blockId: this.id,
            templateProperties: this.templateProperties
        });

        // Detect which template instance is selected
        const templateMap = window.templateElementMap;
        if (templateMap && templateMap[this.id] && templateMap[this.id].element) {
            const element = templateMap[this.id].element;
            const wrapper = element.closest('[data-template-instance]');
            if (wrapper) {
                this.currentTemplateInstanceId = wrapper.dataset.templateInstance;
                console.log('[WYSIWYG] Detected template instance:', this.currentTemplateInstanceId);
            }
        }

        const settings = [];

        for (const prop of this.templateProperties) {
            console.log('[WYSIWYG] Processing property:', prop);

            if (prop.type === 'image') {
                const imageSetting = this.buildImageSetting(prop);
                console.log('[WYSIWYG] Built image setting:', imageSetting);
                settings.push(imageSetting);
            } else if (prop.type === 'select') {
                const selectSetting = this.buildSelectSetting(prop);
                console.log('[WYSIWYG] Built select setting:', selectSetting);
                settings.push(selectSetting);
            } else if (prop.type === 'range') {
                const rangeSetting = this.buildRangeSetting(prop);
                console.log('[WYSIWYG] Built range setting:', rangeSetting);
                settings.push(rangeSetting);
            } else if (prop.type === 'color') {
                const colorSetting = this.buildColorSetting(prop);
                console.log('[WYSIWYG] Built color setting:', colorSetting);
                settings.push(colorSetting);
            } else if (prop.type === 'text') {
                const textSetting = this.buildTextSetting(prop);
                console.log('[WYSIWYG] Built text setting:', textSetting);
                settings.push(textSetting);
            }
        }

        console.log('[WYSIWYG] buildTemplateSettings returning:', {
            blockId: this.id,
            settingsCount: settings.length,
            settings: settings
        });

        return settings;
    }

    /**
     * Get current property value from DOM (instance-aware)
     * Reads actual element attributes instead of cached config values
     */
    getCurrentPropertyValue(propDef) {
        const configValue = this.config[propDef.name];
        const fallback = configValue ?? propDef.default;

        const instanceId = this.currentTemplateInstanceId;
        if (!instanceId) {
            console.log('[WYSIWYG] No instance ID, using fallback for:', propDef.name, fallback);
            return fallback;
        }

        const editorDiv = document.getElementById(`${this.editorId}-editor`);
        if (!editorDiv) {
            console.log('[WYSIWYG] Editor div not found, using fallback for:', propDef.name);
            return fallback;
        }

        const templateWrapper = editorDiv.querySelector(`[data-template-instance="${instanceId}"]`);
        if (!templateWrapper) {
            console.log('[WYSIWYG] Template wrapper not found, using fallback for:', propDef.name);
            return fallback;
        }

        const targetElement = templateWrapper.querySelector(propDef.selector);
        if (!targetElement) {
            console.log('[WYSIWYG] Target element not found with selector:', propDef.selector);
            return fallback;
        }

        let value;
        if (propDef.attribute === 'src') {
            const v = targetElement.getAttribute('src');
            value = (v != null && v !== '') ? v : fallback;
        } else if (propDef.attribute === 'class' && propDef.classPattern) {
            const classes = targetElement.className.split(' ');
            const match = classes.find(c => c.startsWith(propDef.classPattern));
            value = match ?? fallback;
        } else if (propDef.attribute === 'textContent') {
            const v = targetElement.textContent;
            value = (v != null && v !== '') ? v : fallback;
        } else if (propDef.attribute === 'style' && propDef.property) {
            const camelProp = propDef.property.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
            // Try inline style first, then getPropertyValue, then getComputedStyle
            let v = targetElement.style[camelProp];
            if (v == null || v === '') {
                v = targetElement.style.getPropertyValue(propDef.property);
            }
            if (v == null || v === '') {
                v = getComputedStyle(targetElement).getPropertyValue(propDef.property);
            }
            value = (v != null && v !== '') ? v : fallback;
        } else {
            const v = targetElement.getAttribute(propDef.attribute);
            value = (v != null && v !== '') ? v : fallback;
        }

        console.log('[WYSIWYG] Read property value from DOM:', {
            name: propDef.name,
            selector: propDef.selector,
            attribute: propDef.attribute,
            value
        });

        return value;
    }

    /**
     * Build image upload setting
     */
    buildImageSetting(prop) {
        const currentValue = this.getCurrentPropertyValue(prop);
        const escapeHtml = (text) => String(text).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[m]));

        return {
            name: prop.name,
            label: prop.label,
            html: `
                <div class="image-upload-control" style="margin-bottom: 1rem;">
                    <label style="display: block; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem;">
                        ${escapeHtml(prop.label)}
                    </label>

                    <!-- Current Image Preview -->
                    <div style="margin-bottom: 0.75rem;">
                        <img src="${escapeHtml(currentValue)}"
                             style="max-width: 100%; height: auto; max-height: 150px; border-radius: 4px; border: 1px solid #d1d5db;"
                             onerror="this.style.display='none'">
                    </div>

                    <!-- Upload Button -->
                    <input type="file"
                        id="upload-${this.id}-${prop.name}"
                        accept="image/*"
                        @change="uploadTemplateImage($event, '${this.id}', '${prop.name}')"
                        style="display: none;">
                    <button type="button"
                        @click="document.getElementById('upload-${this.id}-${prop.name}').click()"
                        style="background: #3b82f6; color: white; padding: 0.5rem 1rem; border: none; border-radius: 0.375rem; cursor: pointer; width: 100%; font-size: 0.875rem;">
                        📁 Upload New Image
                    </button>
                    <div id="upload-status-${this.id}-${prop.name}"
                         style="font-size: 0.75rem; color: #666; margin-top: 0.5rem;"></div>
                </div>
            `
        };
    }

    /**
     * Build select dropdown setting
     */
    buildSelectSetting(prop) {
        const currentValue = this.getCurrentPropertyValue(prop);
        const escapeHtml = (text) => String(text).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[m]));

        const options = Object.entries(prop.options)
            .map(([value, label]) =>
                `<option value="${escapeHtml(value)}" ${value === currentValue ? 'selected' : ''}>${escapeHtml(label)}</option>`
            )
            .join('');

        return {
            name: prop.name,
            label: prop.label,
            html: `
                <div style="margin-bottom: 1rem;">
                    <label style="display: block; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem;">
                        ${escapeHtml(prop.label)}
                    </label>
                    <select @change="trigger('${this.id}', '${prop.name}', $event.target.value)"
                            style="width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem;">
                        ${options}
                    </select>
                </div>
            `
        };
    }

    /**
     * Build range slider setting
     */
    buildRangeSetting(prop) {
        const rawValue = this.getCurrentPropertyValue(prop);
        const parsed = parseFloat(rawValue);
        const numericValue = isNaN(parsed) ? 0 : parsed;
        const unit = String(rawValue).replace(/^[\d.-]+/, '') || '';
        console.log('[WYSIWYG.buildRangeSetting]', prop.name, { rawValue, parsed, numericValue, unit, min: prop.min, max: prop.max });
        const escapeHtml = (text) => String(text).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[m]));

        return {
            name: prop.name,
            label: prop.label,
            html: `
                <div style="margin-bottom: 1rem;">
                    <label style="display: block; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem;">
                        ${escapeHtml(prop.label)}
                        <span style="font-weight: normal; color: #6b7280;" x-text="'(' + $el.parentElement.nextElementSibling.value + '${unit})'"></span>
                    </label>
                    <input type="range"
                           min="${prop.min}" max="${prop.max}" step="${prop.step}"
                           value="${numericValue}"
                           @input="trigger('${this.id}', '${prop.name}', $event.target.value + '${unit}')"
                           style="width: 100%;">
                </div>
            `
        };
    }

    buildTextSetting(prop) {
        const currentValue = this.getCurrentPropertyValue(prop);
        const escapeHtml = (text) => String(text).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[m]));

        return {
            name: prop.name,
            label: prop.label,
            html: `
                <div style="margin-bottom: 1rem;">
                    <label style="display: block; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem;">
                        ${escapeHtml(prop.label)}
                    </label>
                    <input type="text"
                           value="${escapeHtml(currentValue)}"
                           @input="trigger('${this.id}', '${prop.name}', $event.target.value)"
                           style="width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem;">
                </div>
            `
        };
    }

    buildColorSetting(prop) {
        let currentValue = this.getCurrentPropertyValue(prop);
        // Convert rgb() to hex for <input type="color"> compatibility
        const rgbMatch = String(currentValue).match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
            currentValue = '#' + [rgbMatch[1], rgbMatch[2], rgbMatch[3]].map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
        }
        const escapeHtml = (text) => String(text).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[m]));

        return {
            name: prop.name,
            label: prop.label,
            html: `
                <div style="margin-bottom: 1rem;">
                    <label style="display: block; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem;">
                        ${escapeHtml(prop.label)}
                    </label>
                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                        <input type="color"
                               value="${escapeHtml(currentValue)}"
                               @input="trigger('${this.id}', '${prop.name}', $event.target.value)"
                               style="width: 3rem; height: 2.25rem; padding: 0.125rem; border: 1px solid #d1d5db; border-radius: 0.375rem; cursor: pointer;">
                        <input type="text"
                               value="${escapeHtml(currentValue)}"
                               @input="trigger('${this.id}', '${prop.name}', $event.target.value)"
                               style="flex: 1; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem; font-family: monospace;">
                    </div>
                </div>
            `
        };
    }

    /**
     * Update a template property and apply changes to the HTML
     * Uses querySelectorAll to support one property targeting multiple elements
     * @param {string} propertyName - Property name (e.g., 'backgroundImage')
     * @param {*} value - New value to apply
     */
    updateTemplateProperty(propertyName, value) {
        console.log('[WYSIWYG.updateTemplateProperty] Called:', {
            blockId: this.id,
            propertyName,
            value,
            templateId: this.templateId
        });

        // Find the property definition
        const propDef = this.templateProperties.find(p => p.name === propertyName);
        if (!propDef) {
            console.warn(`[WYSIWYG] Property '${propertyName}' not found in template properties`);
            return;
        }

        // Update config first
        this.config[propertyName] = value;

        // For WYSIWYG blocks with templates, the HTML is in the DOM editor div, not in config.content
        // Find the editor div (contenteditable div created by RichTextLoader)
        const editorDiv = document.getElementById(`${this.editorId}-editor`);
        if (!editorDiv) {
            console.warn(`[WYSIWYG] Editor div not found: ${this.editorId}-editor`);
            return;
        }

        console.log('[WYSIWYG.updateTemplateProperty] Found editor div:', {
            editorId: this.editorId,
            editorDivId: editorDiv.id,
            innerHTML: editorDiv.innerHTML.substring(0, 200)
        });

        // Use instance-scoped selector to avoid affecting other template instances
        const instanceId = this.currentTemplateInstanceId;
        if (!instanceId) {
            console.error('[WYSIWYG] No template instance ID available. Update skipped.');
            return;
        }

        const templateWrapper = editorDiv.querySelector(`[data-template-instance="${instanceId}"]`);
        if (!templateWrapper) {
            console.error(`[WYSIWYG] Template wrapper not found for instance: ${instanceId}`);
            return;
        }

        console.log('[WYSIWYG.updateTemplateProperty] Found template wrapper:', {
            instanceId,
            wrapperId: templateWrapper.id
        });

        // Find all target elements (supports one property on multiple elements)
        const targetElements = templateWrapper.querySelectorAll(propDef.selector);
        if (targetElements.length === 0) {
            console.warn(`[WYSIWYG] Selector '${propDef.selector}' found no elements in template instance. Update skipped.`);
            return;
        }

        console.log('[WYSIWYG.updateTemplateProperty] Found elements:', {
            selector: propDef.selector,
            count: targetElements.length
        });

        // Apply update to all matched elements
        for (const targetElement of targetElements) {
            if (propDef.attribute === 'src') {
                targetElement.setAttribute('src', value);
            } else if (propDef.attribute === 'class' && propDef.classPattern) {
                const classes = targetElement.className.split(' ');
                const filtered = classes.filter(c => !c.startsWith(propDef.classPattern));
                filtered.push(value);
                targetElement.className = filtered.join(' ');
            } else if (propDef.attribute === 'style' && propDef.property) {
                const camelProp = propDef.property.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                targetElement.style[camelProp] = value;
            } else if (propDef.attribute === 'textContent') {
                targetElement.textContent = value;
            } else {
                targetElement.setAttribute(propDef.attribute, value);
            }
        }

        console.log(`[WYSIWYG] Updated ${targetElements.length} element(s) for ${propDef.attribute}`)

        // Sync the updated HTML from the editor div back to the textarea
        // This ensures the changes are saved when the block is saved
        const textarea = document.getElementById(this.editorId);
        if (textarea) {
            // Use RichTextLoader's cleanHTML to remove editor-specific markup
            const richTextLoader = window.richTextLoader;
            if (richTextLoader && richTextLoader.cleanHTML) {
                textarea.value = richTextLoader.cleanHTML(editorDiv.innerHTML);
                console.log('[WYSIWYG] Synced updated HTML to textarea');
            } else {
                textarea.value = editorDiv.innerHTML;
                console.log('[WYSIWYG] Synced updated HTML to textarea (no cleanHTML available)');
            }
        }

        console.log('[WYSIWYG.updateTemplateProperty] Complete. Template updated in live editor.');
    }

    /**
     * Override trigger to handle template property updates
     * Routes template properties to updateTemplateProperty() instead of default config update
     */
    trigger(property, value) {
        console.log('[WYSIWYG.trigger] Called:', {
            blockId: this.id,
            property,
            value,
            hasTemplateProperties: this.hasTemplateProperties,
            hasProperty: this.templateProperties.some(p => p.name === property)
        });

        // If this has template properties and property is in templateProperties, use updateTemplateProperty
        if (this.hasTemplateProperties && this.templateProperties.some(p => p.name === property)) {
            console.log('[WYSIWYG.trigger] Routing to updateTemplateProperty');
            this.updateTemplateProperty(property, value);
        } else {
            // Otherwise use default Tool.js trigger behavior
            console.log('[WYSIWYG.trigger] Using default trigger behavior');
            super.trigger(property, value);
        }
    }

    /**
     * Override serializeConfig to preserve template-specific fields
     * Template properties must persist through save/reload cycles
     */
    serializeConfig(config, seen = new Set()) {
        const serialized = super.serializeConfig(config, seen);

        // Preserve template-specific fields
        if (config.templateId) {
            serialized.templateId = config.templateId;
        }
        if (config.templateProperties) {
            serialized.templateProperties = config.templateProperties;
        }

        // Preserve template property values (backgroundImage, overlayOpacity, etc.)
        if (this.templateProperties && this.templateProperties.length > 0) {
            this.templateProperties.forEach(prop => {
                if (config[prop.name] !== undefined) {
                    serialized[prop.name] = config[prop.name];
                }
            });
        }

        console.log('[WYSIWYG.serializeConfig] Serialized config:', {
            hasTemplateId: !!serialized.templateId,
            hasTemplateProperties: !!serialized.templateProperties,
            propertyValues: this.templateProperties?.map(p => ({
                name: p.name,
                value: serialized[p.name]
            }))
        });

        return serialized;
    }

    render() {
        // For output rendering, just return the content wrapped in the configured format
        return `<${this.config.format} class="wysiwyg-content">${this.config.content}</${this.config.format}>`;
    }
}

export default WYSIWYG;
